/**
 * Publik bosh sahifa — yuqoridan pastga:
 *   1. hero: platforma nomi, qisqa izoh, murojaat/kuzatish/kirish tugmalari, statistika;
 *   2. asosiy xizmatlarga tezkor o'tish qatori;
 *   3. yangiliklar;
 *   4. «Elektron yashil olam» — elektron savdo;
 *   5. tizimni baholash.
 * Futer va yon menyu `PublicLayout` (layout route) da — sahifalar almashganda o'zgarmaydi;
 * qolgan modullar o'z sahifalarida (`SectionPages.tsx`, `ModulePages.tsx`).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { hero } from "@/shared/data/content";
import { usePortalConfig, usePortalHome } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { useReveal } from "@/shared/lib/motion";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useEnterLink } from "@/shared/layouts/PublicLayout";
import { Icon } from "@/shared/ui/Icon";

import { useStatistics } from "./landing/AboutSection";
import { FaqSection } from "./landing/FaqSection";
import { HeroGlobe } from "./landing/HeroGlobe";
import { HeroNotices } from "./landing/HeroNotices";
import type { HeroNotice } from "./landing/HeroNotices";
import { HeroStatsPanel } from "./landing/HeroStatsPanel";
import { HeroBackground, pickAlert } from "./landing/helpers";
import { MarketSection } from "./landing/MarketSection";
import { NewsSection } from "./landing/NewsSection";
import { formatStat } from "./landing/publicData";
import { QuickServices } from "./landing/QuickServices";
import { RatingSection } from "./landing/RatingSection";


export function LandingPage() {
  const { t, lang } = useI18n();
  const home = usePortalHome();
  const config = usePortalConfig();
  const statistics = useStatistics();
  /* Yo'ldosh skanidan keyin globusda chiqadigan ko'rsatkichlar (namunaviy qiymatlar emas). */
  const globeStats = useMemo(
    () =>
      statistics.sample
        ? []
        : statistics.items
            .filter((item) => item.value !== null && item.showOnGlobe !== false)
            .map((item) => ({ value: formatStat(item.value, lang), unit: item.unit, label: item.globeLabel || item.label })),
    [statistics.items, statistics.sample, lang],
  );
  const enter = useEnterLink();
  const alertBanner = pickAlert(home);

  /* Hero ustidagi ogohlantirishlar: avval texnologik tanaffus (ЧТЗ 4.1.1.3),
     so'ng Kuzatuv e'lon qilgan yong'in xavfi. */
  const notices: HeroNotice[] = [];
  if (config.maintenance) {
    notices.push({
      id: "maintenance",
      level: "critical",
      icon: "alert",
      title: "Texnologik tanaffus",
      text:
        config.maintenanceMessage ||
        "Platforma vaqtincha xizmat ko'rsatish rejimida. Ba'zi bo'limlar mavjud emas.",
    });
  }
  if (alertBanner) {
    notices.push({
      id: `banner-${alertBanner.id}`,
      level: alertBanner.level,
      icon: "flame",
      title: alertBanner.title,
      text: alertBanner.text || undefined,
      action: alertBanner.link_url
        ? { label: alertBanner.link_label || t("action.more"), href: alertBanner.link_url }
        : { label: "Xaritada ko'rish", href: SECTION_PATHS.map },
    });
  }

  useReveal([home ? 1 : 0, statistics.items.map((item) => item.code).join(",")]);

  return (
    <>
      <section className="hero">
        <HeroGlobe fallback={<HeroBackground home={home} />} stats={globeStats} />
        <span className="hero-aurora" aria-hidden="true" />
        <HeroNotices notices={notices} />

        <div className="container">
          <div className="hero-inner">
            <h1 className="hero-brand">
              SMART <em>FORESTRY</em>
            </h1>
            <p className="hero-lead">{t("home.lead")}</p>

            <div className="hero-actions">
              <Link className="btn btn-primary btn-lg" to={SECTION_PATHS.appeal}>
                <Icon name="send" size={18} />
                {t("nav.appealSend")}
              </Link>
              <Link className="btn btn-ondark btn-lg" to={SECTION_PATHS.tracking}>
                <Icon name="target" size={18} />
                {t("nav.appealTrack")}
              </Link>
              <Link className="btn btn-ondark btn-lg" to={enter.to}>
                <Icon name="lock" size={18} />
                {t("home.cabinet")}
              </Link>
            </div>

            <p className="hero-note">
              <Icon name="info" size={16} />
              {enter.loggedIn
                ? `Siz tizimga kirgansiz: ${enter.user?.short_name || enter.user?.full_name}. «Kirish» tugmasi kabinetga o'tkazadi.`
                : hero.note}
            </p>
          </div>

          <HeroStatsPanel items={statistics.items} charts={statistics.charts} />
          {statistics.sample ? (
            <p className="hero-sample-note">Namunaviy ko'rsatkichlar (server bilan aloqa yo'q)</p>
          ) : null}
        </div>
      </section>

      <QuickServices />
      <NewsSection />
      <MarketSection />
      <RatingSection />
      <FaqSection />
    </>
  );
}
